/**
 * MuPDF rendering context — provides document loading and page rendering
 * using the MuPDF WASM engine (C-based, 5-10× faster than PDF.js for
 * complex vector content like gradient fills and soft masks).
 *
 * This module runs MuPDF on the main thread. The WASM engine processes
 * PDF operations natively, so even pages with 100k+ vector operations
 * render in ~200-500ms instead of 2-5s.
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const MuPDFContext = createContext(null);

export function useMuPDF() {
  return useContext(MuPDFContext);
}

// Lazy-load mupdf to avoid blocking initial bundle parse
let mupdfModule = null;
let mupdfLoadPromise = null;

export function loadMuPDF() {
  if (mupdfLoadPromise) return mupdfLoadPromise;
  mupdfLoadPromise = import(/* webpackPreload: true */ 'mupdf').then(mod => {
    mupdfModule = mod;
    return mod;
  });
  return mupdfLoadPromise;
}

/**
 * Renders a MuPDF page to a canvas using Pixmap.
 * @param {object} doc - MuPDF Document instance
 * @param {number} pageIndex - 0-based page index
 * @param {HTMLCanvasElement} canvas
 * @param {number} targetHeight - desired CSS pixel height
 * @param {number} pixelRatio - device pixel ratio (1 for fast, 2 for Retina)
 * @returns {{ width: number, height: number, ratio: number }}
 */
export function renderPageToCanvas(doc, pageIndex, canvas, targetHeight, pixelRatio = 1) {
  const mupdf = mupdfModule;
  if (!mupdf || !doc) throw new Error('MuPDF not loaded');

  const page = doc.loadPage(pageIndex);
  const bounds = page.getBounds(); // [x0, y0, x1, y1]
  const pageWidth = bounds[2] - bounds[0];
  const pageHeight = bounds[3] - bounds[1];
  const ratio = pageWidth / pageHeight;

  // Calculate scale to fit targetHeight
  const scale = (targetHeight * pixelRatio) / pageHeight;
  const matrix = mupdf.Matrix.scale(scale, scale);

  // Render to pixmap (RGB, no alpha — white paper background)
  const pixmap = page.toPixmap(matrix, mupdf.ColorSpace.DeviceRGB, false);
  const w = pixmap.getWidth();
  const h = pixmap.getHeight();
  const rgb = pixmap.getPixels(); // 3 bytes per pixel (RGB)

  // Convert RGB → RGBA for ImageData (requires 4 bytes per pixel)
  const totalPixels = w * h;
  const rgba = new Uint8ClampedArray(totalPixels * 4);
  for (let i = 0, j = 0; i < totalPixels; i++, j += 4) {
    const s = i * 3;
    rgba[j]     = rgb[s];
    rgba[j + 1] = rgb[s + 1];
    rgba[j + 2] = rgb[s + 2];
    rgba[j + 3] = 255;
  }

  // Paint onto canvas
  canvas.width = w;
  canvas.height = h;
  canvas.style.width = `${Math.round(w / pixelRatio)}px`;
  canvas.style.height = `${Math.round(h / pixelRatio)}px`;

  const ctx = canvas.getContext('2d');
  const imageData = new ImageData(rgba, w, h);
  ctx.putImageData(imageData, 0, 0);

  // Clean up native resources
  pixmap.destroy();
  page.destroy();

  return { width: w / pixelRatio, height: h / pixelRatio, ratio };
}

/**
 * Provider component that loads a PDF via MuPDF WASM.
 * Children receive the document via useMuPDF() context.
 */
export function MuPDFProvider({ fileData, onLoad, onError, children }) {
  const [state, setState] = useState({ doc: null, loading: true, error: null });
  const docRef = useRef(null);

  useEffect(() => {
    if (!fileData) return;

    let cancelled = false;

    const load = async () => {
      try {
        setState({ doc: null, loading: true, error: null });
        const mupdf = await loadMuPDF();

        if (cancelled) return;

        // fileData can be ArrayBuffer or Uint8Array
        const data = fileData instanceof ArrayBuffer ? new Uint8Array(fileData) : fileData;
        const doc = mupdf.Document.openDocument(data, 'application/pdf');

        if (cancelled) {
          doc.destroy();
          return;
        }

        docRef.current = doc;
        const numPages = doc.countPages();
        setState({ doc, loading: false, error: null, numPages });
        if (onLoad) onLoad({ numPages });
      } catch (err) {
        if (!cancelled) {
          console.error('[MuPDF] Failed to load document:', err);
          setState({ doc: null, loading: false, error: err });
          if (onError) onError(err);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      if (docRef.current) {
        docRef.current.destroy();
        docRef.current = null;
      }
    };
  }, [fileData]);

  return (
    <MuPDFContext.Provider value={state}>
      {children}
    </MuPDFContext.Provider>
  );
}

export default MuPDFContext;
